import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
   CHAT_HISTORY_ERROR_MESSAGES,
   CHAT_UNAVAILABLE_STATUS_CODES,
   ZUSTAND_KEY_UNIQUE,
   USER_INPUT_LIMITS_DEFAULTS,
   ATTACHMENT_DEFAULTS,
   BrowserChatAttachmentPolicy,
   DEFAULT_BROWSER_CHAT_ATTACHMENT_POLICY,
   browserChatAttachmentPolicy,
   chatAttachmentSizeCategory,
   isAllowedChatAttachmentFile
} from "@/app/lib/constants";
import {
   AIModelName,
   AttachmentGroup,
   ChatHistory,
   ChatMessage,
   ConfigItem,
   ChatHistoryPage,
   PendingAttachment,
   SharedChatHistoryResponse
} from "@/types";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { METHOD_TYPE, PROJECTS } from "@/app/lib/enums";
import {
   compareChatHistoryOrder,
   mergeChatHistories,
   sanitizeCollectionName
} from "@/app/lib/utils";
import {
   isSupportedImageSource,
   normalizeAttachmentFile,
   prepareAttachmentImage
} from "@/app/lib/prepareAttachmentImage";
import { uploadChatAttachment } from "@/app/lib/chatAttachmentUpload";

export class ChatHistoryRequestError extends Error {
   readonly status: number;

   constructor(message: string, status: number) {
      super(message);
      this.name = "ChatHistoryRequestError";
      this.status = status;
      Object.setPrototypeOf(this, ChatHistoryRequestError.prototype);
   }

   toJSON() {
      return {
         message: this.message,
         status: this.status,
         name: this.name
      };
   }
}

function getChatHistoryErrorMessage(responseBody: unknown) {
   if (
      typeof responseBody !== "object" ||
      responseBody === null ||
      !("error" in responseBody)
   ) {
      return CHAT_HISTORY_ERROR_MESSAGES.REQUEST_FAILED;
   }

   return typeof responseBody.error === "string"
      ? responseBody.error
      : CHAT_HISTORY_ERROR_MESSAGES.REQUEST_FAILED;
}

async function createChatHistoryRequestError(response: Response) {
   const responseBody: unknown = await response.json().catch(() => null);
   return new ChatHistoryRequestError(
      getChatHistoryErrorMessage(responseBody),
      response.status
   );
}

const CHAT_UNAVAILABLE_STATUS_SET = new Set<number>(CHAT_UNAVAILABLE_STATUS_CODES);
const IMAGE_PROCESSING_ERROR_KEYS = new Set([
   "imageSourceTooLarge",
   "imageDimensionsTooLarge",
   "tooLarge",
   "imageOptimizationFailed"
]);

export function isChatUnavailableError(error: unknown) {
   return (
      error instanceof ChatHistoryRequestError &&
      (CHAT_UNAVAILABLE_STATUS_SET.has(error.status) ||
         error.message.includes(CHAT_HISTORY_ERROR_MESSAGES.HISTORY_NOT_FOUND) ||
         error.message.includes(CHAT_HISTORY_ERROR_MESSAGES.SHARED_HISTORY_NOT_FOUND))
   );
}

const discardUploadedFile = async (fileId: string) => {
   try {
      await fetchWithAuth(api_url.file_url.replace("{id}", fileId), {
         method: METHOD_TYPE.DELETE
      });
   } catch {
      // Deletion is best effort for attachments the user has not sent.
   }
};

const revokeObjectUrls = (attachment: PendingAttachment) => {
   if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
   if (attachment.playbackUrl) URL.revokeObjectURL(attachment.playbackUrl);
};

export const CONFIG_KEYS = {
   PROMPT_CRYPTOCURRENCY_MARKET: "PROMPT_CryptocurrencyMarket",
   PROMPT_KINGS_AND_ALLIES: "PROMPT_KingsAndAllies",
   PROMPT_CRYPTOCURRENCY_FUNCTION_CALLING_INTENT:
      "PROMPT_CRYPTOCURRENCY_FUNCTION_CALLING_INTENT",
   PROMPT_CryptocurrencyMarket_Extend_Function_Calling:
      "PROMPT_CryptocurrencyMarket_Extend_Function_Calling",
   SEARCH_RESULT_LIMITS: "SEARCH_RESULT_LIMITS",
   RERANKER_MODEL: "RERANKER_MODEL",
   model: "model",
   appVersion: "appVersion",
   REASON_MODEL: "REASON_MODEL",
   EXPAND_QUERY_MODEL: "EXPAND_QUERY_MODEL",
   USE_VALIDATOR_AGENT: "USE_VALIDATOR_AGENT",
   MODEL_CONFIGS: "MODEL_CONFIGS",
   MAX_REQUEST_PER_MONTH: "maxRequestPerMonth",
   SEMANTIC_CHUNKING: "SEMANTIC_CHUNKING",
   FALLBACK_APIS_ORDERED: "FALLBACK_APIS_ORDERED",
   CRYPTO_FUNCTION_TOOLS: "cryptoFunctionTools",
   FUNCTION_CALLING_AVAILABLE: "FunctionCallingAvailable",
   USER_INPUT_LIMITS: "USER_INPUT_LIMITS",
   MEMORY_MODULE_CONFIG: "MEMORY_MODULE_CONFIG",
   SOURCE_SUGGESTIONS_CONFIG: "SOURCE_SUGGESTIONS_CONFIG",
   ATTACHMENT_CONFIG: "ATTACHMENT_CONFIG",
   SMOKE_AUTO_RUN_AFTER_DEPLOY: "SMOKE_AUTO_RUN_AFTER_DEPLOY",
   KA_POSTS_CONFIG: "KA_POSTS_CONFIG",
   INSIGHTS_CLUSTER_CONFIG: "INSIGHTS_CLUSTER_CONFIG"
};

interface UsageData {
   requestCount: number;
   maxRequestLimit?: number;
   remainingRequests?: number;
   usagePercentage: number;
   resetDate: string;
   canProceed?: boolean;
   dailyBreakdown?: Array<{
      date: string;
      count: number;
   }>;
}

interface ActiveGeneration {
   id: string;
   timestamp: number;
}

/** Stale entries older than this are auto-purged on read (matches Redis TTL). */
const MAX_GENERATION_AGE_MS = 10 * 60 * 1000; // 10 minutes
export const DEFAULT_CHAT_HISTORY_SIDEBAR_WIDTH = 300;

type HistoryType = {
   // Persisted state:
   isOpenHistoryChat: boolean;
   setOpenHistoryChat: (value: boolean) => void;
   chatHistorySidebarWidth: number;
   setChatHistorySidebarWidth: (value: number) => void;
   historyItem: ChatHistory | null;
   setHistoryItem: (value: ChatHistory | null) => void;
   chatHistories: ChatHistory[];
   isPinnedSectionOpen: boolean;
   setPinnedSectionOpen: (value: boolean) => void;
   isRecentSectionOpen: boolean;
   setRecentSectionOpen: (value: boolean) => void;
   activeGenerations: ActiveGeneration[];
   addActiveGeneration: (id: string) => void;
   removeActiveGeneration: (id: string) => void;
   hasActiveGeneration: (id: string) => boolean;

   // Non-persisted state:
   loadingChat: boolean;
   setLoadingChat: (value: boolean) => void;
   errorChat: string | null;
   setErrorChat: (value: string | null) => void;
   loadingMoreMessageChat: boolean;
   setLoadingMoreMessageChat: (value: boolean) => void;
   messagePageInfo?: {
      total: number;
      currentPage: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
   };
   shouldRefetchHistories: boolean;
   triggerHistoryRefetch: () => void;
   pendingHistoryPatch: Partial<ChatHistory> | null;
   queueHistoryPatch: (item: Partial<ChatHistory>) => void;
   lastSendMessageEvent: {
      key: string | null;
      timestamp: number;
      shiftKey: boolean;
      ctrlKey: boolean;
      altKey: boolean;
      metaKey: boolean;
      text?: string;
   } | null;
   isSearchDialogOpen: boolean;
   setSearchDialogOpen: (value: boolean) => void;
   triggerSendMessageEvent: (
      event:
         | React.KeyboardEvent<HTMLTextAreaElement>
         | React.MouseEvent<HTMLButtonElement, MouseEvent>,
      text?: string
   ) => void;
   limitVisibility: Record<string, boolean>;
   shouldShowLimitForProject: (projectName: PROJECTS | string) => boolean;
   selectedModel: AIModelName;
   setSelectedModel: (value: AIModelName) => void;
   selectedTemperature: number;
   setSelectedTemperature: (value: number) => void;
   selectedStreaming: boolean;
   setSelectedStreaming: (value: boolean) => void;
   maxUserInputLength: number;
   maxFilesPerMessage: number;
   maxRecordingSeconds: number;
   chatAttachmentPolicy: BrowserChatAttachmentPolicy;
   pendingAttachments: PendingAttachment[];
   addPendingAttachments: (files: File[]) => Promise<void>;
   transcribeRecordedVoice: (file: File) => Promise<string | null>;
   removePendingAttachment: (localId: string) => void;
   clearPendingAttachments: () => void;
   initModel: (retryCount?: number) => Promise<ConfigItem | void>;
   streamingResponse: string;
   setStreamingResponse: (value: string) => void;
   currentAttachments: AttachmentGroup[];
   setCurrentAttachments: (value: AttachmentGroup[]) => void;
   loadMoreHistories: (
      userId: string,
      knowledgeBase: PROJECTS[],
      after?: string,
      limit?: number
   ) => Promise<ChatHistoryPage | null>;
   patchChatHistory: (item: Partial<ChatHistory>) => void;
   removeChatHistory: (id: string) => void;
   loadMessages: (
      chatHistoryId: string,
      page?: number,
      limit?: number
   ) => Promise<ChatHistory | null>;
   loadMessagesShared: (
      shareKey: string,
      page?: number,
      limit?: number
   ) => Promise<SharedChatHistoryResponse | null>;
   itemEdit: ChatHistory | null;
   setItemEdit: (value: ChatHistory | null) => void;
   resetState: () => void;

   // Usage-related state:
   usageData: UsageData | null;
   loadingUsage: boolean;
   errorUsage: string | null;
   fetchUsageData: (projectName: string) => Promise<void>;
   triggerUsageRefresh: (projectName: string) => void;

   // Temporary chat state:
   isTemporaryChat: boolean;
   setTemporaryChat: (value: boolean) => void;

   sharedKnowledgeBaseId: string | null;
   setSharedKnowledgeBaseId: (value: string | null) => void;
};

export const useStateHistoryChatPage = create<HistoryType>()(
   persist(
      (set, get) => ({
         messagePageInfo: undefined,
         chatHistories: [],
         // **** Persisted state: *********
         isOpenHistoryChat: true,
         setOpenHistoryChat: (value: boolean) => set({ isOpenHistoryChat: value }),
         chatHistorySidebarWidth: DEFAULT_CHAT_HISTORY_SIDEBAR_WIDTH,
         setChatHistorySidebarWidth: (value: number) =>
            set({ chatHistorySidebarWidth: value }),
         isPinnedSectionOpen: true,
         setPinnedSectionOpen: (value: boolean) => set({ isPinnedSectionOpen: value }),
         isRecentSectionOpen: true,
         setRecentSectionOpen: (value: boolean) => set({ isRecentSectionOpen: value }),
         activeGenerations: [] as ActiveGeneration[],
         addActiveGeneration: (id: string) =>
            set((state) => {
               const now = Date.now();
               // Auto-purge stale entries while adding
               const fresh = state.activeGenerations.filter(
                  (g) => now - g.timestamp < MAX_GENERATION_AGE_MS
               );
               if (fresh.some((g) => g.id === id)) return { activeGenerations: fresh };
               return { activeGenerations: [...fresh, { id, timestamp: now }] };
            }),
         removeActiveGeneration: (id: string) =>
            set((state) => ({
               activeGenerations: state.activeGenerations.filter((g) => g.id !== id)
            })),
         hasActiveGeneration: (id: string) => {
            const now = Date.now();
            return get().activeGenerations.some(
               (g) => g.id === id && now - g.timestamp < MAX_GENERATION_AGE_MS
            );
         },

         // ***** Non-persisted state: ***********
         itemEdit: null,
         setItemEdit: (value: ChatHistory | null) => set({ itemEdit: value }),
         loadingChat: false,
         setLoadingChat: (value: boolean) => set({ loadingChat: value }),
         errorChat: null,
         setErrorChat: (value: string | null) => set({ errorChat: value }),
         loadingMoreMessageChat: false,
         setLoadingMoreMessageChat: (value: boolean) =>
            set({ loadingMoreMessageChat: value }),

         historyItem: null,
         setHistoryItem: (value: ChatHistory | null) => set({ historyItem: value }),
         shouldRefetchHistories: false,
         triggerHistoryRefetch: () => set({ shouldRefetchHistories: true }),
         pendingHistoryPatch: null,
         queueHistoryPatch: (item) => {
            get().patchChatHistory(item);
            set({ pendingHistoryPatch: item });
         },

         lastSendMessageEvent: null,
         triggerSendMessageEvent: (
            event:
               | React.KeyboardEvent<HTMLTextAreaElement>
               | React.MouseEvent<HTMLButtonElement, MouseEvent>,
            text: string = ""
         ) =>
            set({
               lastSendMessageEvent: {
                  key: "key" in event ? event.key : null,
                  timestamp: Date.now(),
                  shiftKey: event.shiftKey,
                  ctrlKey: event.ctrlKey,
                  altKey: event.altKey,
                  metaKey: event.metaKey,
                  text
               }
            }),
         limitVisibility: {},
         shouldShowLimitForProject: (projectName: PROJECTS | string) => {
            const { limitVisibility } = get();
            const sanitizedKey = sanitizeCollectionName(projectName);
            // Default: show limit (backward compatible when no ui config exists)
            return limitVisibility[sanitizedKey] !== false;
         },
         selectedModel: "google/gemini-2.5-flash-lite-preview-09-2025",
         setSelectedModel: (value: AIModelName) => set({ selectedModel: value }),
         selectedTemperature: 0.2,
         setSelectedTemperature: (value: number) => set({ selectedTemperature: value }),
         selectedStreaming: true,
         setSelectedStreaming: (value: boolean) => set({ selectedStreaming: value }),
         maxUserInputLength: USER_INPUT_LIMITS_DEFAULTS.MAX_USER_INPUT_LENGTH,
         maxFilesPerMessage: ATTACHMENT_DEFAULTS.MAX_FILES_PER_MESSAGE,
         maxRecordingSeconds: ATTACHMENT_DEFAULTS.MAX_RECORDING_SECONDS,
         chatAttachmentPolicy: DEFAULT_BROWSER_CHAT_ATTACHMENT_POLICY,
         pendingAttachments: [],
         addPendingAttachments: async (files: File[]) => {
            const { pendingAttachments, maxFilesPerMessage, chatAttachmentPolicy } =
               get();
            const room = maxFilesPerMessage - pendingAttachments.length;
            if (room <= 0) return;

            const selectedFiles = files
               .map(normalizeAttachmentFile)
               .filter((file) => isAllowedChatAttachmentFile(file, chatAttachmentPolicy))
               .slice(0, room);
            const staged: PendingAttachment[] = selectedFiles.map((file) => {
               const category = chatAttachmentSizeCategory(file);
               const maxSizeMB =
                  category === "image"
                     ? file.type === "image/gif"
                        ? chatAttachmentPolicy.maxSizeMB.image
                        : ATTACHMENT_DEFAULTS.MAX_IMAGE_SOURCE_MB
                     : category
                       ? chatAttachmentPolicy.maxSizeMB[category]
                       : 0;
               const tooLarge = !maxSizeMB || file.size > maxSizeMB * 1024 * 1024;
               return {
                  localId: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                  name: file.name,
                  size: file.size,
                  mimetype: file.type,
                  status: tooLarge
                     ? "error"
                     : isSupportedImageSource(file)
                       ? "optimizing"
                       : "uploading",
                  error: tooLarge
                     ? category === "image"
                        ? "imageSourceTooLarge"
                        : "tooLarge"
                     : undefined,
                  maxSizeMB,
                  maxDurationSeconds: file.type.startsWith("audio/")
                     ? chatAttachmentPolicy.maxAudioDurationSeconds
                     : undefined,
                  previewUrl: file.type.startsWith("image/")
                     ? URL.createObjectURL(file)
                     : undefined,
                  playbackUrl: file.type.startsWith("audio/")
                     ? URL.createObjectURL(file)
                     : undefined
               };
            });

            set({ pendingAttachments: [...pendingAttachments, ...staged] });

            const preparedAttachments = await Promise.all(
               staged.map(async (attachment, index) => {
                  if (attachment.status === "error") return null;
                  let prepared = selectedFiles[index];
                  try {
                     if (isSupportedImageSource(prepared)) {
                        prepared = await prepareAttachmentImage(
                           prepared,
                           chatAttachmentPolicy.maxSizeMB.image
                        );
                     }
                  } catch (error) {
                     const message =
                        error instanceof Error &&
                        IMAGE_PROCESSING_ERROR_KEYS.has(error.message)
                           ? error.message
                           : "imageOptimizationFailed";
                     set((state) => ({
                        pendingAttachments: state.pendingAttachments.map((item) =>
                           item.localId === attachment.localId
                              ? { ...item, status: "error", error: message }
                              : item
                        )
                     }));
                     return null;
                  }
                  return { attachment, prepared };
               })
            );

            let totalBytes = pendingAttachments
               .filter((attachment) => attachment.status !== "error")
               .reduce((total, attachment) => total + attachment.size, 0);
            const accepted = preparedAttachments.filter(
               (
                  item
               ): item is {
                  attachment: PendingAttachment;
                  prepared: File;
               } => Boolean(item)
            );

            const uploadable: typeof accepted = [];
            for (const item of accepted) {
               const { attachment, prepared } = item;
               const category = chatAttachmentSizeCategory(prepared);
               let error: string | undefined;
               let maxSizeMB = category
                  ? chatAttachmentPolicy.maxSizeMB[category]
                  : undefined;

               if (
                  totalBytes + prepared.size >
                  chatAttachmentPolicy.maxAttachmentsTotalMB * 1024 * 1024
               ) {
                  error = "attachmentsTotalTooLarge";
                  maxSizeMB = chatAttachmentPolicy.maxAttachmentsTotalMB;
               }

               if (error) {
                  set((state) => ({
                     pendingAttachments: state.pendingAttachments.map((current) =>
                        current.localId === attachment.localId
                           ? { ...current, status: "error", error, maxSizeMB }
                           : current
                     )
                  }));
                  continue;
               }

               totalBytes += prepared.size;
               uploadable.push(item);
            }

            await Promise.all(
               uploadable.map(async ({ attachment, prepared }) => {
                  set((state) => ({
                     pendingAttachments: state.pendingAttachments.map((item) =>
                        item.localId === attachment.localId
                           ? {
                                ...item,
                                name: prepared.name,
                                size: prepared.size,
                                mimetype: prepared.type,
                                status: "uploading",
                                maxSizeMB:
                                   chatAttachmentPolicy.maxSizeMB[
                                      chatAttachmentSizeCategory(prepared) || "image"
                                   ]
                             }
                           : item
                     )
                  }));
                  const uploaded = await uploadChatAttachment(
                     prepared,
                     (status, fileId) =>
                        set((state) => ({
                           pendingAttachments: state.pendingAttachments.map((item) =>
                              item.localId === attachment.localId
                                 ? { ...item, status, fileId: fileId || item.fileId }
                                 : item
                           )
                        }))
                  );
                  const stored = uploaded && "id" in uploaded ? uploaded : null;
                  const uploadError =
                     uploaded && "error" in uploaded ? uploaded.error : "uploadFailed";
                  set((state) => ({
                     pendingAttachments: state.pendingAttachments.map((item) =>
                        item.localId === attachment.localId
                           ? {
                                ...item,
                                status: stored ? "ready" : "error",
                                fileId: stored?.id,
                                error: stored ? undefined : uploadError
                             }
                           : item
                     )
                  }));
               })
            );
         },
         transcribeRecordedVoice: async (file: File) => {
            const uploaded = await uploadChatAttachment(file, () => undefined, true);
            if (!uploaded || !("id" in uploaded)) return null;

            await discardUploadedFile(uploaded.id);
            return uploaded.extractedText?.trim() || null;
         },
         removePendingAttachment: (localId: string) => {
            // Nothing will ever reference this file, so the stored copy goes with it.
            const discarded = get().pendingAttachments.find(
               (item) => item.localId === localId
            );
            if (discarded?.fileId) void discardUploadedFile(discarded.fileId);

            set((state) => {
               const target = state.pendingAttachments.find(
                  (item) => item.localId === localId
               );
               if (target) revokeObjectUrls(target);
               return {
                  pendingAttachments: state.pendingAttachments.filter(
                     (item) => item.localId !== localId
                  )
               };
            });
         },
         clearPendingAttachments: () =>
            set((state) => {
               state.pendingAttachments.forEach(revokeObjectUrls);
               return { pendingAttachments: [] };
            }),
         initModel: async (retryCount = 0) => {
            const MAX_INIT_RETRIES = 2;
            try {
               const modelUrl = new URL(api_url.config_url, window.location.origin);
               modelUrl.searchParams.append("key", CONFIG_KEYS.model);

               const maxRequestUrl = new URL(api_url.config_url, window.location.origin);
               maxRequestUrl.searchParams.append(
                  "key",
                  CONFIG_KEYS.MAX_REQUEST_PER_MONTH
               );

               const userInputLimitsUrl = new URL(
                  api_url.config_url,
                  window.location.origin
               );
               userInputLimitsUrl.searchParams.append(
                  "key",
                  CONFIG_KEYS.USER_INPUT_LIMITS
               );

               const attachmentConfigUrl = new URL(
                  api_url.config_url,
                  window.location.origin
               );
               attachmentConfigUrl.searchParams.append(
                  "key",
                  CONFIG_KEYS.ATTACHMENT_CONFIG
               );

               const [
                  modelResponse,
                  maxRequestResponse,
                  userInputLimitsResponse,
                  attachmentConfigResponse
               ] = await Promise.all([
                  fetchWithAuth(modelUrl.toString(), { method: METHOD_TYPE.GET }),
                  fetchWithAuth(maxRequestUrl.toString(), { method: METHOD_TYPE.GET }),
                  fetchWithAuth(userInputLimitsUrl.toString(), {
                     method: METHOD_TYPE.GET
                  }),
                  fetchWithAuth(attachmentConfigUrl.toString(), {
                     method: METHOD_TYPE.GET
                  })
               ]);

               const modelData = modelResponse.ok ? await modelResponse.json() : null;
               const maxRequestData = maxRequestResponse.ok
                  ? await maxRequestResponse.json()
                  : null;
               const userInputLimitsData = userInputLimitsResponse.ok
                  ? await userInputLimitsResponse.json()
                  : null;
               const attachmentConfigData = attachmentConfigResponse.ok
                  ? await attachmentConfigResponse.json()
                  : null;

               // Retry if backend returned an empty successful response (cache cold-start)
               const hasModelData =
                  modelData?.data !== undefined && modelData?.data !== null;
               const hasInputLimitsData =
                  userInputLimitsData?.data !== undefined &&
                  userInputLimitsData?.data !== null;
               const shouldRetryInit =
                  retryCount < MAX_INIT_RETRIES &&
                  ((modelResponse.ok && !hasModelData) ||
                     (userInputLimitsResponse.ok && !hasInputLimitsData));
               if (shouldRetryInit) {
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  return get().initModel(retryCount + 1);
               }

               // Empty on purpose: the config endpoint withholds the model name from a
               // non-admin, and substituting one here would be a guess.
               let modelValue = "";
               let tempValue = 0.2;
               let streamValue = true;

               if (typeof modelData?.data === "object" && modelData?.data !== null) {
                  if (modelData.data.model) modelValue = modelData.data.model;
                  if (typeof modelData.data.temperature === "number") {
                     tempValue = modelData.data.temperature;
                  }
                  if (typeof modelData.data.streaming === "boolean") {
                     streamValue = modelData.data.streaming;
                  }
               } else if (typeof modelData?.data === "string") {
                  modelValue = modelData.data;
               }

               const inputLimits =
                  typeof userInputLimitsData?.data === "object" &&
                  userInputLimitsData?.data !== null
                     ? userInputLimitsData.data
                     : {};

               const attachmentLimits =
                  typeof attachmentConfigData?.data === "object" &&
                  attachmentConfigData?.data !== null
                     ? attachmentConfigData.data
                     : {};
               const configuredMaxFiles = Number(attachmentLimits.maxFilesPerMessage);
               const configuredMaxRecordingSeconds = Number(
                  attachmentLimits.maxRecordingSeconds
               );

               // Parse UI visibility flags from filtered config response
               const uiFlags = maxRequestData?.data?.ui;
               const parsedVisibility: Record<string, boolean> = {};
               if (uiFlags && typeof uiFlags === "object") {
                  for (const [key, val] of Object.entries(uiFlags)) {
                     if (typeof val === "object" && val !== null && "showLimit" in val) {
                        parsedVisibility[key] = (val as { showLimit: boolean }).showLimit;
                     }
                  }
               }

               set({
                  selectedModel: modelValue as AIModelName,
                  selectedTemperature: tempValue,
                  selectedStreaming: streamValue,
                  limitVisibility: parsedVisibility,
                  maxUserInputLength: inputLimits.maxUserInputLength
                     ? Number(inputLimits.maxUserInputLength)
                     : USER_INPUT_LIMITS_DEFAULTS.MAX_USER_INPUT_LENGTH,
                  maxFilesPerMessage:
                     Number.isInteger(configuredMaxFiles) && configuredMaxFiles > 0
                        ? Math.min(
                             configuredMaxFiles,
                             ATTACHMENT_DEFAULTS.MAX_FILES_PER_MESSAGE
                          )
                        : ATTACHMENT_DEFAULTS.MAX_FILES_PER_MESSAGE,
                  maxRecordingSeconds:
                     Number.isFinite(configuredMaxRecordingSeconds) &&
                     configuredMaxRecordingSeconds > 0
                        ? Math.min(
                             configuredMaxRecordingSeconds,
                             ATTACHMENT_DEFAULTS.MAX_RECORDING_SECONDS
                          )
                        : ATTACHMENT_DEFAULTS.MAX_RECORDING_SECONDS,
                  chatAttachmentPolicy: browserChatAttachmentPolicy(attachmentLimits)
               });

               return;
            } catch (error) {
               console.error("Error fetching configs:", error);
               set({
                  selectedModel: "",
                  selectedTemperature: 0.2,
                  selectedStreaming: true,
                  limitVisibility: {},
                  maxUserInputLength: USER_INPUT_LIMITS_DEFAULTS.MAX_USER_INPUT_LENGTH,
                  maxFilesPerMessage: ATTACHMENT_DEFAULTS.MAX_FILES_PER_MESSAGE,
                  maxRecordingSeconds: ATTACHMENT_DEFAULTS.MAX_RECORDING_SECONDS,
                  chatAttachmentPolicy: DEFAULT_BROWSER_CHAT_ATTACHMENT_POLICY
               });
               return;
            }
         },
         isSearchDialogOpen: false,
         setSearchDialogOpen: (value: boolean) => set({ isSearchDialogOpen: value }),

         streamingResponse: "",
         setStreamingResponse: (value: string) => set({ streamingResponse: value }),
         currentAttachments: [],
         setCurrentAttachments: (value: AttachmentGroup[]) =>
            set({ currentAttachments: value }),
         loadMoreHistories: async (
            userId: string,
            knowledgeBase: PROJECTS[] = [],
            after?: string,
            limit: number = 10
         ) => {
            if (!userId) return null;
            const url = new URL(api_url.chat_history_url, window.location.origin);
            url.searchParams.append("userId", userId);
            url.searchParams.append("knowledgeBase", JSON.stringify(knowledgeBase));
            url.searchParams.append("limit", limit.toString());
            if (after) url.searchParams.append("after", after);

            const response = await fetchWithAuth(url.toString(), {
               method: METHOD_TYPE.GET
            });
            if (!response.ok) {
               throw new Error("Failed to fetch data");
            }
            const { data } = await response.json();
            if (!data || !data.items.length) return null;

            set((state) => ({
               chatHistories: after
                  ? mergeChatHistories(state.chatHistories, data.items)
                  : data.items
            }));

            return data;
         },
         patchChatHistory: (item: Partial<ChatHistory>) =>
            set((state) => ({
               chatHistories: state.chatHistories
                  .map((history) =>
                     history.id === item.id ? { ...history, ...item } : history
                  )
                  .sort(compareChatHistoryOrder)
            })),
         removeChatHistory: (id: string) =>
            set((state) => ({
               chatHistories: state.chatHistories.filter(
                  (history) => history.id !== id
               )
            })),
         loadMessages: async (
            chatHistoryId: string,
            page: number = 1,
            limit: number = 10
         ): Promise<ChatHistory | null> => {
            if (!chatHistoryId) return null;
            const url = new URL(
               api_url.chat_history_messages_url,
               window.location.origin
            );
            url.searchParams.append("chatHistoryId", chatHistoryId);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", limit.toString());

            const response = await fetchWithAuth(url.toString(), {
               method: METHOD_TYPE.GET,
               cache: "no-store"
            });
            if (!response.ok) {
               throw await createChatHistoryRequestError(response);
            }
            const { data } = await response.json();
            if (!data) return null;

            // Sort messages by timestamp in ascending order (oldest first)
            data.messages?.items?.sort((a: ChatMessage, b: ChatMessage) => {
               const timeA = new Date(a.timestamp || Date.now()).getTime();
               const timeB = new Date(b.timestamp || Date.now()).getTime();
               return timeA - timeB;
            });
            return data;
         },
         loadMessagesShared: async (
            shareKey: string,
            page: number = 1,
            limit: number = 10
         ) => {
            if (!shareKey) return null;
            const url = new URL(api_url.chat_history_share_url, window.location.origin);
            url.searchParams.append("shareKey", shareKey);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", limit.toString());

            const response = await fetchWithAuth(url.toString(), {
               method: METHOD_TYPE.GET
            });
            if (!response.ok) {
               throw await createChatHistoryRequestError(response);
            }
            const { data } = await response.json();
            if (!data || !data?.messages?.items?.length) return null;
            return data;
         },
         resetState: () => {
            set({
               messagePageInfo: undefined,
               chatHistories: [],
               isOpenHistoryChat: true,
               itemEdit: null,
               loadingChat: false,
               errorChat: null,
               loadingMoreMessageChat: false,
               historyItem: null,
               activeGenerations: [] as ActiveGeneration[],
               shouldRefetchHistories: false,
               pendingHistoryPatch: null,
               lastSendMessageEvent: null,
               streamingResponse: "",
               currentAttachments: [],
               isTemporaryChat: false,
               sharedKnowledgeBaseId: null
            });
         },

         // Usage-related state:
         usageData: null,
         loadingUsage: false,
         errorUsage: null,
         fetchUsageData: async (projectName: string) => {
            if (!projectName) return;
            try {
               set({ usageData: null, loadingUsage: true, errorUsage: null });

               const url = new URL(api_url.usage_url, window.location.origin);
               url.searchParams.append("projectName", projectName);

               const response = await fetchWithAuth(url.toString(), {
                  method: METHOD_TYPE.GET
               });

               if (!response.ok) {
                  throw new Error("Failed to fetch usage data");
               }

               const data = await response.json();
               set({ usageData: data.data });
            } catch (err: any) {
               console.error("[Chat Store] Error fetching usage data:", {
                  projectName,
                  error: err
               });
               set({
                  errorUsage: err.message || "Failed to load usage information"
               });
            } finally {
               set({ loadingUsage: false });
            }
         },
         triggerUsageRefresh: (projectName: string) => {
            const { fetchUsageData } = get();
            fetchUsageData(projectName);
         },

         // Temporary chat (non-persisted):
         isTemporaryChat: false,
         setTemporaryChat: (value: boolean) => set({ isTemporaryChat: value }),

         sharedKnowledgeBaseId: null,
         setSharedKnowledgeBaseId: (value: string | null) =>
            set({ sharedKnowledgeBaseId: value })
      }),
      {
         name: ZUSTAND_KEY_UNIQUE.CHAT_PAGE,
         storage: createJSONStorage(() => localStorage),
         // Only persist specified properties
         partialize: (state) => ({
            isOpenHistoryChat: state.isOpenHistoryChat,
            chatHistorySidebarWidth: state.chatHistorySidebarWidth,
            isPinnedSectionOpen: state.isPinnedSectionOpen,
            isRecentSectionOpen: state.isRecentSectionOpen,
            chatHistories: state.chatHistories,
            usageData: state.usageData,
            activeGenerations: state.activeGenerations
         }),
         // Migration: handle old string[] format → { id, timestamp }[]
         // Also purge stale entries on hydration to prevent phantom reconnects
         // after page reload (especially on iOS Safari where pages can be
         // fully unloaded and reloaded with stale localStorage data)
         merge: (persisted, current) => {
            const merged = { ...current, ...(persisted as Partial<HistoryType>) };
            if (Array.isArray(merged.activeGenerations)) {
               const now = Date.now();
               merged.activeGenerations = (merged.activeGenerations as any[])
                  .map((item: any) =>
                     typeof item === "string" ? { id: item, timestamp: 0 } : item
                  )
                  .filter(
                     (g: ActiveGeneration) =>
                        g.timestamp > 0 && now - g.timestamp < MAX_GENERATION_AGE_MS
                  );
            }
            return merged;
         }
      }
   )
);
